import CardCommon from '@op/components/CardCommon';
import { barIcon, lineIcon, pieIcon } from '@op/constants/chart';
import { gameInfoType } from '@op/types/common';
import React, { useEffect, useMemo, useState } from 'react';
import { Col, Row } from 'tea-component';
import { useIntl } from 'umi';
import BarLineChart from '../../components/BarLineChart';
import PieChart from '../../components/PieChart';
import HBarChart from '../../components/HBarChart';
import { getKolBrandProfile } from '@op/services/influencer';
import './index.scss';
import { NO_DATE } from '@op/constants';
import { useDownloadParams } from '@op/hooks/useDownloadParams';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';
import BubbleCommon, {
  brandOverviewTips,
  influencerProfileTipsText,
} from '@op/components/BubbleCommon';
import { influencerColorMap, influencerSortMap } from '../../constants';
import useLanguage from '@op/hooks/useLanguage';
import SortBySelect from '@op/components/FilterBoxCommon/filters/single-select-factory';

export default function InfluencerProfile({
  filter,
  gameInfo,
  mediaType,
}: {
  filter: any;
  gameInfo: gameInfoType;
  mediaType: string;
}) {
  const [type, setType] = useState<'pie' | 'line' | 'bar'>('pie');
  const { formatMessage, formatOptionsMessage } = useLanguage();
  const [pie, setPie] = useState([]);
  const [timeline, setTimeline] = useState<{
    timeline_of_start_time: string;
    timeline_of_end_time: string;
    timeline: Array<any>;
  }>({
    timeline_of_start_time: '',
    timeline_of_end_time: '',
    timeline: [],
  });
  const [channelBar, setChannelBar] = useState([]);
  const [loading, setLoading] = useState(false);
  const { downloadLegacy } = useDownloadParams();
  const [countsDownloading, setCountsDownloading] = useState(false);
  const [channelDownloading, setChannelDownloading] = useState(false);
  const [sortBy, setSortBy] = useState<{
    value: string;
  }>({
    value: 'follower',
  });

  const params = useMemo(
    () => ({
      ...filter,
      metrics: ['timeline', 'list', 'channel'],
      metrics_classify_by: sortBy.value,
    }),
    [filter, sortBy],
  );

  useEffect(() => {
    console.log('params', params);
    setLoading(true);
    setPie([]);
    setTimeline({
      timeline_of_start_time: '',
      timeline_of_end_time: '',
      timeline: [],
    });
    setChannelBar([]);
    getKolBrandProfile(params)
      .then((data: any) => {
        const {
          list,
          timeline,
          channel,
          timeline_of_start_time,
          timeline_of_end_time,
        } = data;

        setPie(list || []);

        setTimeline({
          timeline_of_start_time,
          timeline_of_end_time,
          timeline: timeline || [],
        });
        setChannelBar(channel || []);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [params]);

  const translate = (text: string) =>
    formatMessage({ id: text || NO_DATE.EMPTY });

  const download = async (sheetName = 'Influencer Counts') => {
    buriedBtnPoint('IBT_Download', sheetName);
    const isChannel = sheetName === 'Channel Distribution';
    if (isChannel) {
      setChannelDownloading(true);
    } else {
      setCountsDownloading(true);
    }
    try {
      await downloadLegacy({
        limit: 150000,
        params,
        downloadFunc: 'getKolBrandProfile',
        sheetName: sheetName,
        simpleName: sheetName,
      });
    } finally {
      if (isChannel) {
        setChannelDownloading(false);
      } else {
        setCountsDownloading(false);
      }
    }
  };

  return (
    <CardCommon
      title={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {formatMessage({ id: 'Influencer Profile' })}
        </div>
      }
      className="influencer-profile-wrapper"
    >
      <Row>
        <Col>
          <CardCommon
            contentCard
            title={
              <BubbleCommon tipsMap={brandOverviewTips} placement={'right'}>
                {formatMessage({ id: 'Influencer Counts' })}
              </BubbleCommon>
            }
            operations={[barIcon, lineIcon, pieIcon]}
            hasDownload
            downloadLoading={countsDownloading}
            defaultOperation="pie"
            loading={loading}
            loadingHeight={330}
            handleOperationsOnClick={(item) => setType(item.symbol)}
            handleDownloadOnClick={download}
            subTitle={
              <SortBySelect
                defaultValue={sortBy.value}
                onChange={(key: string, value: any) => {
                  setSortBy({ value });
                }}
                defaultParams={{
                  options: [
                    {
                      value: 'follower',
                      text: (
                        <BubbleCommon
                          tipsMap={influencerProfileTipsText}
                          placement={'right'}
                          // childStyle={{ width: '100%', paddingRight: '12px' }} // 让气泡展示在选项外
                        >
                          {formatMessage({ id: 'By Followers' })}
                        </BubbleCommon>
                      ),
                    },
                    {
                      value: 'view',
                      text: formatMessage({ id: 'By Views' }),
                    },
                    {
                      value: 'engagement',
                      text: formatMessage({ id: 'By Engagement' }),
                    },
                  ],
                  needFormatMessage:false,
                  matchButtonWidth: true,
                  dealwithButton: (value: any, { textMap }: any) =>
                    textMap[value],
                }}
              />
            }
          >
            {type === 'pie' ? (
              <PieChart
                data={pie}
                filter={filter}
                translate={translate}
                sortMap={influencerSortMap}
                colorMap={influencerColorMap}
                title={formatMessage({ id: 'Influencer Counts' })}
              ></PieChart>
            ) : (
              <BarLineChart
                type={type}
                translate={translate}
                data={timeline}
                sortMap={influencerSortMap}
                colorMap={influencerColorMap}
                title={formatMessage({ id: 'Influencer Counts' })}
                dateType={filter.date_type}
              ></BarLineChart>
            )}
          </CardCommon>
        </Col>
        <Col>
          <CardCommon
            title={formatMessage({ id: 'Channel Distribution' })}
            contentCard
            hasDownload
            downloadLoading={channelDownloading}
            loading={loading}
            loadingHeight={330}
            handleDownloadOnClick={() => download('Channel Distribution')}
          >
            <HBarChart
              filter={filter}
              title={formatMessage({ id: 'Channel Distribution' })}
              translate={translate}
              data={channelBar}
              colorMap={influencerColorMap}
              sortMap={influencerSortMap}
            ></HBarChart>
          </CardCommon>
        </Col>
      </Row>
    </CardCommon>
  );
}
